import React from "react";
import { Button, Card } from "react-bootstrap";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Doughnut } from "react-chartjs-2";
import { shallowEqual, useSelector } from "react-redux";
import DateRangePicker from "react-bootstrap-daterangepicker";
import {
  customRangeReport,
  stringToDateWithFormat
} from "../../../utils/utils";
import moment from "moment";
import { YYYY_MM_DD_HH_MM_SS } from "../../../utils/const";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import SVG from "react-inlinesvg";
import { useState } from "react";
import { useClearingAccountReportUIContext } from "../Clearing Account Report/ClearingAccountReportUIContext";
import { useMemo } from "react";
import { isEqual } from "lodash";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const initialValues = {
  transactionDate: "thismonth",
  dateFilterLabel: "This Month"
};

const ClearingAccountChart = () => {
  const clearingAccountReportUIContext = useClearingAccountReportUIContext();
  const clearingAccountReportUIProps = useMemo(() => {
    return {
      queryParamsChart: clearingAccountReportUIContext.queryParamsChart,
      setqueryParamsChart: clearingAccountReportUIContext.setqueryParamsChart
    };
  }, [clearingAccountReportUIContext]);

  const [chartFilter, setChartFilter] = useState(initialValues);
  const { currentState } = useSelector(
    (state) => ({ currentState: state.clearingAccountReport }),
    shallowEqual
  );
  const { chartEntities, chartLoading } = currentState || {};
  const { Revenue, transaction } = chartEntities || {};

  const data = {
    labels: ["Pending", "Approved", "Accepted", "Rejected"],
    datasets: [
      {
        data: transaction,
        backgroundColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)"
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)"
        ],
        labels: ["Pending", "Approved", "Accepted", "Rejected"],
        label: "Transactions",
        datalabels: {
          anchor: "end"
        }
      },
      {
        data: Revenue,
        backgroundColor: [
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
          "rgba(192, 42, 75,1)",
          "rgba(42, 192, 122,1)"
        ],
        borderColor: [
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
          "rgba(192, 42, 75,1)",
          "rgba(42, 192, 122,1)"
        ],
        labels: ["Pending", "Approved", "Accepted", "Rejected"],
        label: "Revenue",
        datalabels: {
          anchor: "start"
        }
      }
    ]
  };

  const options = {
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let datasetLabel = context.dataset.label || "";
            const label = context.label;
            const value = context.formattedValue;
            return "  " + datasetLabel + " - " + label + " : " + value;
          }
        }
      },
      datalabels: {
        backgroundColor: function(context) {
          return context.dataset.backgroundColor;
        },
        borderColor: "white",
        borderRadius: 25,
        borderWidth: 2,
        color: "white",
        display: function(context) {
          var dataset = context.dataset;
          var value = dataset.data[context.dataIndex];
          return value;
        },
        font: {
          weight: "bold"
        },
        padding: 6
      }
    },
    // Core options
    aspectRatio: 4 / 3,
    cutoutPercentage: 32,
    layout: {
      padding: 32
    },
    elements: {
      line: {
        fill: false
      },
      point: {
        hoverRadius: 7,
        radius: 5
      }
    }
  };

  const applyFilter = (values) => {
    const newQueryParams = prepareFilter(
      clearingAccountReportUIProps.queryParamsChart,
      values
    );
    if (
      !isEqual(newQueryParams, clearingAccountReportUIProps.queryParamsChart)
    ) {
      clearingAccountReportUIProps.setqueryParamsChart(newQueryParams);
    }
  };

  const prepareFilter = (queryParams, values) => {
    const { transactionDate, dateFrom, dateTo } = values;
    const newQueryParams = { ...queryParams };
    const filter = {};

    filter.transactionDate =
      transactionDate !== "" ? transactionDate : undefined;
    filter.dateFrom = dateFrom !== "" ? dateFrom : undefined;
    filter.dateTo = dateTo !== "" ? dateTo : undefined;

    newQueryParams.filter = filter;
    return newQueryParams;
  };

  return (
    <Card className="card-custom card-stretch make-payment-wrapper mb-4">
      <Card.Header className="align-items-center border-bottom mt-4  d-lg-flex">
        <h3 className="card-title align-items-start flex-column">
          <span className="font-weight-bold text-dark">
            Transactions Statistics
          </span>
        </h3>
        <DateRangePicker
          onApply={(event, picker) => {
            const { chosenLabel } = picker || {};
            const fromDate = picker.startDate._d.toISOString();
            const endDate = picker.endDate._d.toISOString();
            let label = chosenLabel;
            if (label === "Today") {
              label = "today";
            }
            if (label === "Yesterday ") {
              label = "yesterday";
            }
            if (label === "Last 7 Days") {
              label = "last7days";
            }
            if (label === "Last 30 Days") {
              label = "last30days";
            }
            if (label === "This Week") {
              label = "thisweek";
            }
            if (label === "This Month") {
              label = "thismonth";
            }
            if (label === "This Year") {
              label = "thisyear";
            }
            if (label === "Custom Range") {
              label = "customrange";
            }
            const dateFrom = stringToDateWithFormat(fromDate);
            const dateEnd = stringToDateWithFormat(endDate);
            const dateFromMoment = moment(dateFrom).format(YYYY_MM_DD_HH_MM_SS);
            const dateEndMoment = moment(dateEnd).format(YYYY_MM_DD_HH_MM_SS);
            const chartFtr = {
              transactionDate: label.toLowerCase(),
              dateFilterLabel: chosenLabel,
              dateFrom: dateFromMoment,
              dateTo: dateEndMoment
            };
            setChartFilter(chartFtr);
            applyFilter(chartFtr);
          }}
          initialSettings={{
            startDate: moment().startOf("month"),
            endDate: moment().endOf("month"),
            ranges: customRangeReport,
            opens: "left",
            applyButtonClasses: "btn btn-brand",
            showCustomRangeLabel: false
          }}
        >
          <Button variant="btn btn-brand font-weight-bolder btn-md">
            <span className="svg-icon svg-icon-sm svg-icon-primary">
              <SVG
                src={toAbsoluteUrl("/media/svg/icons/General/Calendar.svg")}
              ></SVG>
            </span>
            {chartFilter.dateFilterLabel}
            {chartLoading && (
              <span className="ml-1 mr-3 spinner spinner-white"></span>
            )}
          </Button>
        </DateRangePicker>
      </Card.Header>

      <Card.Body>
        <div className="d-flex justify-content-left responsive">
          <div style={{ width: "500px" }}>
            <Doughnut data={data} options={options} />
          </div>
          <div class="responsive" >
          <h1 >  
              Description
          </h1>
          <div className="spacer-class"></div>
          <div class = "row"><div className="colorbox-yellow" ></div> &nbsp; : &nbsp; <h6> Transactions Accepted </h6></div>
          <div className="spacer-columns"></div>
          <div class = "row"><div className="colorbox-pink" ></div> &nbsp; : &nbsp; <h6> Transactions Pending </h6></div>
          <div className="spacer-columns"></div>
          <div class = "row"><div className="colorbox-blue" ></div> &nbsp; : &nbsp; <h6> Transactions Approved By Unity </h6></div>
          <div className="spacer-columns"></div>
          <div class = "row"><div className="colorbox-cyan" ></div> &nbsp; : &nbsp; <h6> Transactions Rejected By User </h6></div>
          <div className="spacer-columns"></div>
          <div class = "row"><div className="colorbox-red" ></div> &nbsp; : &nbsp; <h6> Revenue Accepted </h6></div>
          <div className="spacer-columns"></div>
          <div class = "row"><div className="colorbox-purple" ></div> &nbsp; : &nbsp; <h6> Revenue Pending </h6></div>
          <div className="spacer-columns"></div>
          <div class = "row"><div className="colorbox-orange" ></div> &nbsp; : &nbsp; <h6> Revenue Approved By Unity </h6></div>
          <div className="spacer-columns"></div>
          <div class = "row"><div className="colorbox-green" ></div> &nbsp; : &nbsp; <h6> Revenue Rejected </h6></div>
          <div className="spacer-columns"></div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ClearingAccountChart;
